/**
 * Split hours: a day's opening hours with optional breaks inside them, e.g.
 * open 11:00–22:00 with a break 15:00–17:00.
 *
 * A day is stored as its outer range (first opening to last closing) so that
 * consumers which only read `start`/`end` still get a valid, if slightly
 * generous, answer. Breaks are stored alongside it, and the true open periods
 * are derived here.
 *
 * Times are minutes since midnight (0–1439). A day whose end is at or before its
 * start runs past midnight; `start === end` is a full 24-hour day.
 *
 * Dependency-free and shared by the frontend (editor and displays) and the
 * backend (`localTimes.periods`), so the two can never disagree about which
 * breaks are valid.
 */

const MINUTES_PER_DAY = 24 * 60;

export interface MinuteRange {
  start: number;
  end: number;
}

/** Minutes after the day's opening time, wrapping past midnight. */
const minutesAfterOpening = (opening: number, time: number) =>
  (((time - opening) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;

const getDayLength = (hours: MinuteRange) =>
  minutesAfterOpening(hours.start, hours.end) || MINUTES_PER_DAY;

/**
 * A break is valid when it starts after the day opens, ends after it starts,
 * and ends before the day closes. A break touching either edge would really be
 * a change to the opening or closing time.
 */
export const isBreakWithinHours = (
  hours: MinuteRange,
  dayBreak: MinuteRange,
): boolean => {
  const breakStart = minutesAfterOpening(hours.start, dayBreak.start);
  const breakEnd = minutesAfterOpening(hours.start, dayBreak.end);
  return (
    breakStart > 0 && breakStart < breakEnd && breakEnd < getDayLength(hours)
  );
};

/**
 * The periods a business is actually open on a day, in order. Invalid or
 * overlapping breaks are ignored rather than rejected, so bad stored data can
 * never hide a day's hours.
 */
export const getOpenPeriods = (
  hours: MinuteRange,
  breaks: MinuteRange[] = [],
): MinuteRange[] => {
  const dayLength = getDayLength(hours);
  const relativeBreaks = breaks
    .filter(dayBreak => isBreakWithinHours(hours, dayBreak))
    .map(dayBreak => ({
      start: minutesAfterOpening(hours.start, dayBreak.start),
      end: minutesAfterOpening(hours.start, dayBreak.end),
    }))
    .sort((a, b) => a.start - b.start);

  const periods: MinuteRange[] = [];
  let cursor = 0;
  for (const dayBreak of relativeBreaks) {
    if (dayBreak.start < cursor) continue;
    periods.push({ start: cursor, end: dayBreak.start });
    cursor = dayBreak.end;
  }
  periods.push({ start: cursor, end: dayLength });

  return periods.map(period => ({
    start: (hours.start + period.start) % MINUTES_PER_DAY,
    end: (hours.start + period.end) % MINUTES_PER_DAY,
  }));
};

/**
 * A sensible first suggestion when a break is added: the classic restaurant
 * 15:00–17:00 if it fits, otherwise an hour (or half hour) in the middle of the
 * day on the 30-minute grid. `null` when the day is too short to hold a break.
 */
export const getDefaultBreak = (hours: MinuteRange): MinuteRange | null => {
  const afternoon = { start: 15 * 60, end: 17 * 60 };
  if (isBreakWithinHours(hours, afternoon)) return afternoon;

  const dayLength = getDayLength(hours);
  for (const duration of [60, 30]) {
    const offset = Math.round((dayLength - duration) / 2 / 30) * 30;
    const candidate = {
      start: (hours.start + offset) % MINUTES_PER_DAY,
      end: (hours.start + offset + duration) % MINUTES_PER_DAY,
    };
    if (isBreakWithinHours(hours, candidate)) return candidate;
  }
  return null;
};

/** Parses `HH:mm` into minutes since midnight. */
export const parseTimeOfDayMinutes = (value?: string | null): number | null => {
  const match = value?.match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

/** Formats minutes since midnight as `HH:mm`. */
export const formatTimeOfDayMinutes = (minutes: number): string => {
  const normalized =
    ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(normalized / 60))}:${pad(normalized % 60)}`;
};
